import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useDepartments } from '@/features/departments/api/use-departments';
import { useCreateEmployee } from '@/features/employees/api/use-employees';
import { useEmployeeAccess } from '@/features/employees/hooks/use-employee-access';
import { usePlatformFacilities } from '@/features/facilities/api/use-facilities';
import { useSubsidiaries } from '@/features/settings/subsidiaries/api/use-subsidiaries';
import { useInviteMember } from '@/features/team/api/use-invite-member';
import { useRoles } from '@/hooks/use-roles';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/employees/new')({
  component: NewEmployeePage,
});

function NewEmployeePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { canManageEmployees, isLoading: accessLoading, orgId } = useEmployeeAccess();
  const createMutation = useCreateEmployee();
  const { roles, isLoading: rolesLoading } = useRoles({ orgId });
  const inviteMutation = useInviteMember({ orgId: orgId ?? '' });

  const { data: deptData } = useDepartments({ page_size: '200' });
  const { data: facilityData } = usePlatformFacilities({ page_size: '200' });
  const { data: subsidiariesData } = useSubsidiaries({ includeNested: true });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [externalId, setExternalId] = useState('');
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [inviteToPlatform, setInviteToPlatform] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');

  const canSubmit =
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    (!inviteToPlatform || selectedRoleId.length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await createMutation.mutateAsync({
        name: name.trim(),
        email: email.trim(),
        departmentId: departmentId || undefined,
        facilityId: facilityId || undefined,
        employeeIdExternal: externalId.trim() || undefined,
        organizationAccessIds: selectedOrgIds.length > 0 ? selectedOrgIds : undefined,
      });

      if (inviteToPlatform && selectedRoleId) {
        try {
          const result = await inviteMutation.mutateAsync({
            emailAddresses: [email.trim()],
            roleId: selectedRoleId,
          });
          if (result.invitations.length > 0) {
            toast.success(i18n.t('settings:toast.employeeCreatedAndPlatformInvitationSent'));
          } else {
            const reason = result.failed[0]?.reason ?? 'Unknown error';
            toast.warning(i18n.t('settings:toast.employeeCreatedButInvitationFailed', { reason: reason }));
          }
        } catch {
          toast.warning(i18n.t('settings:toast.employeeCreatedButPlatformInvitationFailed'));
        }
      } else {
        toast.success(i18n.t('settings:toast.employeeCreated'));
      }

      void navigate({ to: '/employees' });
    } catch {
      toast.error(i18n.t('settings:toast.failedToCreateEmployee'));
    }
  };

  if (accessLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!canManageEmployees) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-12 text-center">
          <h2 className="text-lg font-semibold">{t('accessDenied.title')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('accessDenied.employeeManageDenied')}
          </p>
          <Button
            variant="outline"
            onClick={() => void navigate({ to: '/dashboard' })}
            className="mt-4"
          >
            {t('accessDenied.goToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));
  const departmentOptions = [
    { value: '', label: t('employees.selectDepartment') },
    ...(deptData?.items.filter((d) => d.isActive).map((d) => ({ value: d.id, label: d.name })) ??
      []),
  ];
  const facilityOptions = [
    { value: '', label: t('employees.selectFacility') },
    ...(facilityData?.items
      .filter((f) => f.status === 'active')
      .map((f) => ({ value: f.id, label: f.name })) ?? []),
  ];
  const subsidiaries = subsidiariesData?.subsidiaries ?? [];
  const hasSubsidiaries = subsidiaries.length > 0;

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void navigate({ to: '/employees' })}
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> {t('employees.back')}
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{t('employees.addEmployeeTitle')}</h1>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.employeeDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  {t('employees.name')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  placeholder={t('employees.fullName')}
                  value={name}
                  onChange={(e) => { setName(e.target.value); }}
                  required
                  data-testid="employee-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">
                  {t('employees.email')} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('employees.emailPlaceholder')}
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); }}
                  required
                  data-testid="employee-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="department">{t('employees.department')}</Label>
                <NativeSelect
                  id="department"
                  options={departmentOptions}
                  value={departmentId}
                  onChange={setDepartmentId}
                  data-testid="employee-department-select"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="facility">{t('employees.facility')}</Label>
                <NativeSelect
                  id="facility"
                  options={facilityOptions}
                  value={facilityId}
                  onChange={setFacilityId}
                  data-testid="employee-facility-select"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="externalId">{t('employees.externalId')}</Label>
              <Input
                id="externalId"
                placeholder={t('employees.hrSystemEmployeeId')}
                value={externalId}
                onChange={(e) => { setExternalId(e.target.value); }}
                data-testid="employee-external-id-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Company Access */}
        {hasSubsidiaries && (
          <Card>
            <CardHeader>
              <CardTitle>{t('employees.companyAccess')}</CardTitle>
              <CardDescription>
                {t('employees.companyAccessDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {subsidiaries.map((sub) => (
                  <label
                    key={sub.id}
                    className="flex items-center gap-3 rounded-md border p-3 hover:bg-accent/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.includes(sub.id)}
                      onChange={(e) => {
                        setSelectedOrgIds((prev) =>
                          e.target.checked
                            ? [...prev, sub.id]
                            : prev.filter((id) => id !== sub.id)
                        );
                      }}
                      className="h-4 w-4 rounded border-input"
                      data-testid={`org-access-${sub.id}`}
                    />
                    <div>
                      <p className="text-sm font-medium">{sub.name}</p>
                      {sub.level > 1 && (
                        <p className="text-xs text-muted-foreground">
                          {'└'.padStart(sub.level, ' ')} Level {sub.level} subsidiary
                        </p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Platform Access */}
        <Card>
          <CardHeader>
            <CardTitle>{t('employees.platformAccess')}</CardTitle>
            <CardDescription>
              {t('employees.platformAccessDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t('employees.inviteAsPlatformMember')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('employees.sendEmailInvitation')}
                </p>
              </div>
              <Switch
                checked={inviteToPlatform}
                onCheckedChange={setInviteToPlatform}
                data-testid="invite-toggle"
              />
            </div>

            {inviteToPlatform && (
              <div className="space-y-2">
                <Label htmlFor="role">
                  {t('employees.roleRequired')} <span className="text-destructive">*</span>
                </Label>
                <NativeSelect
                  id="role"
                  options={roleOptions}
                  value={selectedRoleId}
                  onChange={setSelectedRoleId}
                  placeholder={t('employees.selectRole')}
                  disabled={rolesLoading}
                  data-testid="role-select"
                />
                <p className="text-xs text-muted-foreground">
                  {t('employees.invitationRoleHint')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void navigate({ to: '/employees' })}
          >
            {t('employees.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={!canSubmit || createMutation.isPending || inviteMutation.isPending}
            data-testid="save-employee-button"
          >
            {createMutation.isPending || inviteMutation.isPending
              ? t('employees.creating')
              : inviteToPlatform
                ? t('employees.createAndInvite')
                : t('employees.createEmployee')}
          </Button>
        </div>
      </form>
    </div>
  );
}
